import { Platform } from 'react-native';
import { EventChannel, eventChannel } from 'redux-saga';
import { call, cancel, fork, put, take, takeLatest } from 'redux-saga/effects';

import type { Purchase, PurchaseError } from '../../services/iap';
import { iapService } from '../../services/iap';
import { axiosInstance } from '../../utils/axiosInstance';
import { getUserFacingHttpErrorKey } from '../../utils/helpers/httpErrorKey';
import { profileRequest } from '../auth/authActions';
import {
	iapDisconnect,
	iapFetchProductsFailure,
	iapFetchProductsRequest,
	iapFetchProductsSuccess,
	iapFetchSubscriptionsFailure,
	iapFetchSubscriptionsRequest,
	iapFetchSubscriptionsSuccess,
	iapInitFailure,
	iapInitRequest,
	iapInitSuccess,
	iapPurchaseError,
	iapPurchaseFailure,
	iapPurchaseRequest,
	iapPurchaseUpdate,
	iapVerifyPurchaseFailure,
	iapVerifyPurchaseRequest,
	iapVerifyPurchaseSuccess,
	USER_CANCELLED_PURCHASE,
} from './iapActions';

// react-native-iap can report cancellation with different codes depending on platform/version.
const isUserCancelled = (error: { code?: string } | null | undefined) =>
	error?.code === 'E_USER_CANCELLED' || error?.code === 'user-cancelled';

function getStoreProductId(product: any): string | undefined {
	return product?.productId ?? product?.id ?? product?.sku;
}

function summarizePurchase(purchase: Purchase | null | undefined) {
	if (!purchase) {
		return null;
	}

	return {
		productId: purchase.productId,
		transactionId: purchase.transactionId,
		purchaseToken: purchase.purchaseToken,
		transactionDate: purchase.transactionDate,
		hasTransactionReceipt: Boolean((purchase as any).transactionReceipt),
	};
}

function summarizeError(error: any) {
	if (!error) {
		return null;
	}

	return {
		name: error.name,
		code: error.code,
		message: error.message,
		debugMessage: error.debugMessage,
		responseStatus: error.response?.status,
		responseData: error.response?.data,
	};
}

function hasValidStoreKitJws(purchase: Purchase | null | undefined): boolean {
	const token = purchase?.purchaseToken;

	if (typeof token !== 'string') {
		return false;
	}

	const parts = token.split('.');
	return parts.length === 3 && parts.every(part => part.length > 0);
}

/**
 * Initialize IAP connection
 */
function* initializeIAP() {
	try {
		console.log('[IAP Saga] Initializing IAP...');
		const success: boolean = yield call([iapService, iapService.initialize]);

		if (success) {
			yield put(iapInitSuccess());
			console.log('[IAP Saga] IAP initialized successfully');
		} else {
			yield put(iapInitFailure('Failed to initialize IAP connection'));
		}
	} catch (error: any) {
		console.error('[IAP Saga] Init error:', error);
		yield put(iapInitFailure(error.message || 'Failed to initialize IAP'));
	}
}

/**
 * Fetch available products
 */
function* fetchProducts(action: ReturnType<typeof iapFetchProductsRequest>) {
	try {
		console.log('[IAP Saga] Fetching products:', action.payload);
		const products = yield call([iapService, iapService.getAvailableProducts], action.payload);
		yield put(iapFetchProductsSuccess(products));
		console.log('[IAP Saga] Products fetched:', {
			count: products.length,
			productIds: products.map((product: any) => getStoreProductId(product)),
		});
	} catch (error: any) {
		console.error('[IAP Saga] Fetch products error:', error);
		yield put(iapFetchProductsFailure(error.message || 'Failed to fetch products'));
	}
}

/**
 * Fetch available subscriptions
 */
function* fetchSubscriptions(action: ReturnType<typeof iapFetchSubscriptionsRequest>) {
	try {
		console.log('[IAP Saga] Fetching subscriptions:', action.payload);
		const subscriptions = yield call(
			[iapService, iapService.getAvailableSubscriptions],
			action.payload,
		);
		yield put(iapFetchSubscriptionsSuccess(subscriptions));
		console.log('[IAP Saga] Subscriptions fetched:', {
			count: subscriptions.length,
			productIds: subscriptions.map((subscription: any) => getStoreProductId(subscription)),
		});
	} catch (error: any) {
		console.error('[IAP Saga] Fetch subscriptions error:', error);
		yield put(iapFetchSubscriptionsFailure(error.message || 'Failed to fetch subscriptions'));
	}
}

/**
 * Purchase a product or subscription
 */
function* purchaseProduct(action: ReturnType<typeof iapPurchaseRequest>) {
	try {
		const { productId, pkg, isSubscription } = action.payload;
		console.log('[IAP Saga] Starting purchase:', {
			productId,
			isSubscription,
			packageId: pkg.id,
			packageName: pkg.name,
			packageType: pkg.type,
			price: pkg.price,
		});

		if (typeof productId !== 'string' || productId.trim().length === 0) {
			throw new Error(`Missing IAP productId for package ${pkg.id}`);
		}

		const normalizedProductId = productId.trim();
		const availableProducts = isSubscription
			? yield call([iapService, iapService.getAvailableSubscriptions], [normalizedProductId])
			: yield call([iapService, iapService.getAvailableProducts], [normalizedProductId]);

		const matchingProduct = availableProducts.find(
			(availableProduct: any) => getStoreProductId(availableProduct) === normalizedProductId,
		);

		if (!matchingProduct) {
			console.error('[IAP Saga] Requested SKU was not returned by the store:', {
				platform: Platform.OS,
				requestedProductId: normalizedProductId,
				availableProductIds: availableProducts.map((availableProduct: any) =>
					getStoreProductId(availableProduct),
				),
			});
			throw new Error(
				`Store did not return IAP SKU ${normalizedProductId}. Check App Store / Play Console setup and backend metadata.`,
			);
		}

		console.log('[IAP Saga] Store returned requested SKU:', {
			platform: Platform.OS,
			requestedProductId: normalizedProductId,
			matchedProductId: getStoreProductId(matchingProduct),
		});

		if (Platform.OS === 'ios' && !isSubscription) {
			const pendingPurchases: Purchase[] = yield call([
				iapService,
				iapService.getPendingPurchasesIOS,
			]);
			const pendingPurchase = pendingPurchases.find(
				purchase => purchase.productId === normalizedProductId,
			);

			// That earlier purchase was the buyer's own, so it is never verified as a donation.
			if (pendingPurchase && action.payload.recipientPlayerId) {
				yield put(
					iapPurchaseFailure(
						'An earlier purchase of this package is still being completed. Try the donation again once it has been delivered to you.',
					),
				);
				return;
			}

			if (pendingPurchase) {
				console.log(
					'[IAP Saga] Found pending iOS purchase for requested SKU, verifying it instead of requesting a new purchase:',
					{
						requestedProductId: normalizedProductId,
						purchase: summarizePurchase(pendingPurchase),
					},
				);
				yield put(iapPurchaseUpdate(pendingPurchase));
				return;
			}
		}

		if (isSubscription) {
			yield call([iapService, iapService.purchaseSubscription], normalizedProductId);
		} else {
			yield call([iapService, iapService.purchaseProduct], normalizedProductId);
		}

		console.log('[IAP Saga] Purchase request sent, waiting for update...');
		// The actual purchase update will come through the listener
	} catch (error: any) {
		console.error('[IAP Saga] Purchase error:', summarizeError(error));

		if (isUserCancelled(error)) {
			yield put(iapPurchaseFailure(USER_CANCELLED_PURCHASE));
		} else {
			yield put(iapPurchaseFailure(error.message || 'Purchase failed'));
		}
	}
}

/**
 * Create event channel for purchase updates
 */
function createPurchaseChannel(): EventChannel<{
	type: 'update' | 'error';
	data: Purchase | PurchaseError;
}> {
	return eventChannel(emitter => {
		iapService.setupPurchaseListeners(
			(purchase: Purchase) => {
				emitter({ type: 'update', data: purchase });
			},
			(error: PurchaseError) => {
				emitter({ type: 'error', data: error });
			},
		);

		// Return unsubscribe function
		return () => {
			iapService.removePurchaseListeners();
		};
	});
}

/**
 * Watch purchase channel
 */
function* watchPurchaseChannel() {
	const channel: EventChannel<any> = yield call(createPurchaseChannel);

	try {
		while (true) {
			const event: { type: 'update' | 'error'; data: Purchase | PurchaseError } =
				yield take(channel);

			if (event.type === 'update') {
				const purchase = event.data as Purchase;
				console.log('[IAP Saga] Purchase update received:', summarizePurchase(purchase));
				yield put(iapPurchaseUpdate(purchase));

				// Auto-verify the purchase
				// We need to get the package info - it should be in the state
				// For now, we'll trigger verification which will handle it
			} else if (event.type === 'error') {
				const error = event.data as PurchaseError;
				console.error('[IAP Saga] Purchase error received:', summarizeError(error));
				// The purchase sheet's own cancel arrives here (not as a thrown error) on Android.
				if (isUserCancelled(error)) {
					yield put(iapPurchaseFailure(USER_CANCELLED_PURCHASE));
				} else {
					yield put(iapPurchaseError(error));
				}
			}
		}
	} finally {
		channel.close();
	}
}

/**
 * Verify purchase with backend
 */
function* verifyPurchase(action: ReturnType<typeof iapVerifyPurchaseRequest>) {
	try {
		const { purchase, pkg, recipientPlayerId } = action.payload;
		console.log('[IAP Saga] Verifying purchase with backend:', {
			platform: Platform.OS,
			purchase: summarizePurchase(purchase),
			packageId: pkg.id,
			packageName: pkg.name,
			packageType: pkg.type,
		});

		let transactionReceipt: string | null = null;
		if (Platform.OS === 'ios') {
			if (hasValidStoreKitJws(purchase)) {
				console.log(
					'[IAP Saga] iOS verification will use purchase token / transaction JWS; App Store receipt is not required for this purchase',
					{
						productId: purchase.productId,
						transactionId: purchase.transactionId,
					},
				);
			} else {
				console.log(
					'[IAP Saga] iOS purchase token was missing or not a valid JWS, fetching App Store receipt as fallback',
					{
						productId: purchase.productId,
						transactionId: purchase.transactionId,
						hasPurchaseToken: Boolean(purchase.purchaseToken),
					},
				);
				transactionReceipt = yield call([iapService, iapService.getReceiptIOS]);
				if (!transactionReceipt) {
					console.warn(
						'[IAP Saga] App Store receipt fallback was unavailable; proceeding with available purchase token / transaction data only',
						{
							productId: purchase.productId,
							transactionId: purchase.transactionId,
							hasPurchaseToken: Boolean(purchase.purchaseToken),
						},
					);
				}
			}
		}

		// Prepare receipt data
		const receiptData = {
			transactionId: purchase.transactionId,
			productId: purchase.productId,
			purchaseToken: purchase.purchaseToken,
			transactionReceipt, // iOS only
			platform: Platform.OS,
			packageId: pkg.id,
			packageType: pkg.type,
			// A chip donation: credited to the recipient, answered without a `newBalance`.
			...(recipientPlayerId && pkg.type === 'CHIPS' ? { recipientPlayerId } : {}),
		};

		console.log('[IAP Saga] Sending receipt data to backend:', {
			transactionId: receiptData.transactionId,
			productId: receiptData.productId,
			hasPurchaseToken: Boolean(receiptData.purchaseToken),
			hasTransactionReceipt: Boolean(receiptData.transactionReceipt),
			transactionReceiptLength: receiptData.transactionReceipt?.length ?? 0,
			platform: receiptData.platform,
			packageId: receiptData.packageId,
			packageType: receiptData.packageType,
			environmentIOS: (purchase as any).environmentIOS,
			appBundleIdIOS: (purchase as any).appBundleIdIOS,
			originalTransactionIdentifierIOS: (purchase as any).originalTransactionIdentifierIOS,
		});

		// Send to backend for verification
		const response = yield call(
			[axiosInstance, axiosInstance.post],
			'/api/store/iap/verify',
			receiptData,
		);

		console.log('[IAP Saga] Backend verification response:', {
			success: response.data?.success,
			message: response.data?.message,
			error: response.data?.error,
			newBalance: response.data?.newBalance,
			raw: response.data,
		});

		if (response.data.success) {
			// Finish the transaction
			if (pkg.type === 'CHIPS') {
				yield call([iapService, iapService.finishTransaction], purchase, true);
				if (Platform.OS === 'android') {
					yield call([iapService, iapService.consumePurchaseAndroid], purchase);
				}
			} else if (pkg.type === 'VIP_MEMBERSHIP') {
				if (Platform.OS === 'android') {
					yield call([iapService, iapService.acknowledgePurchaseAndroid], purchase);
				} else {
					yield call([iapService, iapService.finishTransaction], purchase, true);
				}
			}

			yield put(
				iapVerifyPurchaseSuccess({
					purchase,
					pkg,
					newBalance: response.data.newBalance,
				}),
			);

			yield put(profileRequest());

			console.log('[IAP Saga] Purchase verified and completed successfully');
		} else {
			throw new Error(response.data.message || 'Verification failed');
		}
	} catch (error: any) {
		console.error('[IAP Saga] Verification error:', summarizeError(error));
		yield put(
			iapVerifyPurchaseFailure({
				error: getUserFacingHttpErrorKey(error, {
					serverUnavailableKey: 'T_PURCHASE_VERIFICATION_UNAVAILABLE',
					authKey: 'T_SESSION_EXPIRED_RELOGIN',
					networkKey: 'T_NETWORK_ERROR_RETRY',
					clientErrorKey: 'T_PURCHASE_VERIFICATION_FAILED',
					fallbackKey: 'T_PURCHASE_VERIFICATION_FAILED',
				}),
				purchase: action.payload.purchase,
			}),
		);
	}
}

/**
 * Disconnect IAP
 */
function* disconnectIAP() {
	try {
		console.log('[IAP Saga] Disconnecting IAP...');
		yield call([iapService, iapService.disconnect]);
		console.log('[IAP Saga] IAP disconnected');
	} catch (error) {
		console.error('[IAP Saga] Disconnect error:', error);
	}
}

/**
 * Root saga
 */
export function* iapSaga() {
	// Start listening to purchase updates
	const purchaseChannelTask = yield fork(watchPurchaseChannel);

	// Watch for actions
	yield takeLatest(iapInitRequest.type, initializeIAP);
	yield takeLatest(iapFetchProductsRequest.type, fetchProducts);
	yield takeLatest(iapFetchSubscriptionsRequest.type, fetchSubscriptions);
	yield takeLatest(iapPurchaseRequest.type, purchaseProduct);
	yield takeLatest(iapVerifyPurchaseRequest.type, verifyPurchase);
	yield takeLatest(iapDisconnect.type, function* () {
		yield cancel(purchaseChannelTask);
		yield call(disconnectIAP);
	});
}
